import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    TextField,
    Typography
} from '@mui/material';
import { post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

// ─── Schedule Callback Dialog ───────────────────────────────────────────────────

export function ScheduleCallbackDialog({ open, onClose, activeContact, agentId, agentName, apiKey }) {
    const [callBackTime, setCallBackTime] = useState('');
    const [notes, setNotes] = useState('');
    const [releasable, setReleasable] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [minTime, setMinTime] = useState('');

    useEffect(() => {
        if (open && activeContact) {
            // Default and minimum: 15 minutes from now in local time
            const thirtyMinsOut = new Date(Date.now() + 15 * 60 * 1000);
            const formatted = formatLocalDateTime(thirtyMinsOut);
            setCallBackTime(formatted);
            setMinTime(formatted);
            setNotes('');
            setReleasable(false);
        }
    }, [open, activeContact]);

    /** Format a Date to a local datetime-local string (YYYY-MM-DDTHH:mm) */
    function formatLocalDateTime(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    /** Convert a local datetime-local string to a full ISO 8601 string for the backend */
    function toISOString(localDateTimeStr) {
        return new Date(localDateTimeStr).toISOString();
    }

    async function handleSubmit() {
        if (!callBackTime || !activeContact) return;
        setSubmitting(true);
        try {
            const response = await post({
                apiName: 'cdyxoutreach',
                path: '/cache/createCallback',
                options: {
                    headers: {
                        Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
                        'x-api-key': apiKey,
                    },
                    body: {
                        campaignId: activeContact.campaignId,
                        segmentId: activeContact.segmentId,
                        contactId: activeContact.contactId || activeContact.id,
                        createdById: agentId,
                        createdByAgentName: agentName || '',
                        callBackPOC: activeContact.lastDialedNumber,
                        callDateTime: toISOString(callBackTime),
                        agentId: agentId,
                        note: notes,
                        releasable: releasable,
                    },
                },
            }).response;
            const result = await response.body?.json();
            if (result?.rescheduled) {
                enqueueSnackbar('Callback rescheduled successfully', { variant: 'success' });
            } else {
                enqueueSnackbar('Callback scheduled successfully', { variant: 'success' });
            }
            onClose(true);
        } catch (error) {
            console.error('Error scheduling callback', error);
            let errMsg = null;
            try {
                if (error?.response?.body) {
                    const errBody = await error.response.body.json();
                    errMsg = errBody?.msg || errBody?.message || null;
                }
            } catch (e) {
                // Could not parse error body — fall through to default message
            }
            enqueueSnackbar(errMsg || 'Failed to schedule callback', { variant: 'error' });
        }
        setSubmitting(false);
    }

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogContent>
                <Card elevation={0}>
                    <CardHeader
                        title={
                            activeContact
                                ? `Schedule Callback — ${activeContact.firstName || ''} ${activeContact.lastName || ''}`.trim()
                                : 'Schedule Callback'
                        }
                    />
                    <CardContent>
                        {!activeContact && (
                            <Typography color="text.secondary">
                                No active contact available. Work a contact in Preview, Click2Call, or Manual Dialing to schedule a callback.
                            </Typography>
                        )}
                        {activeContact && (
                            <Box display="flex" flexDirection="column" gap={2}>
                                {/* Contact confirmation details */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: '8px',
                                        bgcolor: 'action.hover',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    {activeContact.firstName && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Name</Typography>
                                            <Typography variant="body2">{`${activeContact.firstName || ''} ${activeContact.lastName || ''}`.trim()}</Typography>
                                        </Box>
                                    )}
                                    {activeContact.lastDialedNumber && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Callback Number</Typography>
                                            <Typography variant="body2">{activeContact.lastDialedNumber}</Typography>
                                        </Box>
                                    )}
                                    {activeContact.phone && activeContact.phone !== activeContact.lastDialedNumber && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Phone</Typography>
                                            <Typography variant="body2">{activeContact.phone}</Typography>
                                        </Box>
                                    )}
                                    {activeContact.cell && activeContact.cell !== activeContact.lastDialedNumber && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Cell</Typography>
                                            <Typography variant="body2">{activeContact.cell}</Typography>
                                        </Box>
                                    )}
                                    {activeContact.externalId && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>External ID</Typography>
                                            <Typography variant="body2">{activeContact.externalId}</Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Divider />

                                <TextField
                                    label="Callback Date/Time"
                                    variant="outlined"
                                    type="datetime-local"
                                    value={callBackTime}
                                    onChange={(e) => setCallBackTime(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: minTime }}
                                    helperText="Must be at least 15 minutes from now"
                                    fullWidth
                                />
                                <TextField
                                    multiline
                                    rows={3}
                                    label="Notes"
                                    variant="outlined"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value.slice(0, 256))}
                                    inputProps={{ maxLength: 256 }}
                                    helperText={`${notes.length}/256`}
                                />
                                {/* <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={releasable}
                                                onChange={(e) => setReleasable(e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Typography variant="caption" color="text.secondary">
                                                Release contact back to the campaign if not dialled by end of the scheduled day
                                            </Typography>
                                        }
                                    /> */}
                            </Box>
                        )}
                    </CardContent>
                    {activeContact && (
                        <CardActions>
                            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={submitting || !callBackTime}
                            >
                                {submitting ? <CircularProgress size={20} /> : 'Schedule'}
                            </Button>
                        </CardActions>
                    )}
                </Card>
            </DialogContent>
        </Dialog>
    );
}